import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useGreenhouseLocations from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useGreenhouseLocations';

import type { IJobSearch, IJobSearchComponent } from './types';
import { Wrapper } from '@klv/utils/wrapper';
import { getBackgroundType } from '@klv/utils/wrapper/functions';
import * as styles from './styles/job-search.module.scss';
import { generateClassName } from '@klv/shared/utils/functions';
import Eyebrow from '@klv/atoms/eyebrow';
import Icon from '@klv/atoms/icon';
import Button from '@klv/atoms/button';
import Buttons from '@klv/molecules/buttons';
import Image from '@klv/atoms/image';
import BaseGrid from '@klv/utils/grid/styles';

export const JobSearchComponent = ({
  backgroundColor,
  bodyCopy = '',
  buttons,
  customPadding = { customPaddingTop: 'none', customPaddingBottom: 'none' },
  eyebrow,
  headline = '',
  locations,
  image,
}: IJobSearchComponent) => {
  const { t } = useTranslation();

  const keywordInput = useRef<HTMLInputElement | null>(null);
  const locationInput = useRef<HTMLSelectElement | null>(null);
  const hasLightBackground = backgroundColor !== 'lightSand' && getBackgroundType(backgroundColor) === 'light';
  const jobSearchBackgroundColor = !backgroundColor || backgroundColor === 'lightSand' ? 'lavenderPoppy' : backgroundColor;

  const searchJobs = (e: React.SyntheticEvent) => {
    e.preventDefault();

    const url = new URL('careers/search-jobs', 'https://www.klaviyo.com');
    if (keywordInput?.current?.value)
      url.searchParams.set('keywords', keywordInput?.current?.value || '');
    
    if (locationInput?.current?.value)
      url.searchParams.set('location', locationInput?.current?.value || '');

    window.location.assign(url.href.replace(url.origin, ''));
  };

  return (
    <Wrapper className={styles.styledWrapper} backgroundColor={jobSearchBackgroundColor} customPadding={customPadding || { customPaddingTop: 'md', customPaddingBottom: 'md' }}>
      <BaseGrid className={styles.jobSearchGrid}>
        <div className={styles.headlineWrapper}>
          {eyebrow && <Eyebrow content={eyebrow} textColor={hasLightBackground ? 'black' : 'white'} />}
          <h1 className={generateClassName([styles.jobSearchHeadline, 'h1', 'semiBold', !!eyebrow ? styles.hasEyebrow : ''])}>{headline}</h1>
          {bodyCopy && (
            <p className={styles.jobSearchBody} dangerouslySetInnerHTML={{ __html: bodyCopy }} />
          )}
        </div>
        <form className={generateClassName([styles.jobSearchForm, hasLightBackground ? styles.showBorder : ''])} onSubmit={searchJobs}>
          <div className={styles.jobSearchField}>
            <Icon className={styles.jobSearchIcon} size={2.5} iconName='Search'/>
            <input className={styles.jobSearchInput} ref={keywordInput} type="text" placeholder={t('organisms_jobSearch_searchJobs')}/>
          </div>
          <div className={styles.jobSearchField}>
            <Icon className={styles.jobSearchIcon} size={2.5} iconName='MapMarker'/>
            <select className={generateClassName([styles.jobSearchInput])} ref={locationInput} name="location" id="searchJobsForm-location" >
              <option value="">{t('organisms_jobSearch_allLocations')}</option>
              {locations.map((location) => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>
          <Button type="submit" variant="primary" text={t('organisms_jobSearch_search')}/>
        </form>
        <Buttons
          className={styles.styledButtons}
          buttons={buttons}
          buttonColor={hasLightBackground ? 'dark' : 'light'}
          variants={['primary', 'ghost']}
        />
        {!!image?.gatsbyImage && 
          <Image className={styles.styledImage} imageData={image.gatsbyImage} altText={image.altText} />
        }
      </BaseGrid>
    </Wrapper>
  );
};

const JobSearch = ({
  background,
  backgroundColor,
  bodyCopy = '',
  buttons,
  customPadding,
  eyebrow,
  headline = '',
  image,
  ...props
}: IJobSearch) => {
  const locations = useGreenhouseLocations();

  return <JobSearchComponent 
    backgroundColor={backgroundColor}
    bodyCopy={bodyCopy}
    buttons={buttons}
    customPadding={customPadding}
    eyebrow={eyebrow}
    headline={headline}
    image={image}
    locations={locations}
    {...props}
  />;
};

export default JobSearch;
